import React from 'react';
import styled from 'styled-components';

/**
 * @property {string} text Text to display for the selection
 * @property {string} selectedText Text to display when selected
 * @property {string} selected Determine if the button is selected
 * @property {() => void} onSelectedCallback Callback when the button is pressed
 * @property {"link" | "btn"} onSelectedCallback determine the type to display
 * @property {boolean} isLoading Add loading state
 */
export interface AccountSelectionButton {
  text: string;
  selectedText?: string;
  selected?: boolean;
  onSelectedCallback: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'link' | 'btn';
  disabled?: boolean;
  isLoading?: boolean;
}

/**
 * A button used to select accounts
 * @param {AccountSelectionButton} props
 * @returns {JSX.Element} Account Selection Button
 */
export default function AccountSelectionButton({
  text,
  selectedText,
  selected = false,
  onSelectedCallback,
  type = 'btn',
  disabled,
  isLoading = false,
}: AccountSelectionButton) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isLoading) return;
    onSelectedCallback(e);
  };

  if (type === 'link') {
    return (
      <button type="button" className="btn-link" onClick={handleClick} disabled={disabled || isLoading}>
        {text}
      </button>
    );
  }

  return (
    <div className="account-selection-wrapper">
      {selected ? (
        <SelectedBadge>
          <i className="fa fa-check" /> {selectedText}
        </SelectedBadge>
      ) : (
        <SelectionButton onClick={handleClick} disabled={disabled || isLoading}>
          {isLoading ? 'Selecting...' : text}
        </SelectionButton>
      )}
    </div>
  );
}

const SelectionButton = styled.button`
  width: 100%;
  min-width: 80px;
  padding: 15px 20px;
  font-weight: bold;
  text-transform: uppercase;
  border-radius: 100px;
  transition: all 0.2s ease-in-out;
  background: transparent;
  border: 1px solid var(--blue);

  color: var(--blue);
  cursor: pointer;
  border: 2px solid rgba(255, 255, 255, 0.5); /* Lighten up border */
  min-height: 40px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: white;
    scale: 1.02;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SelectedBadge = styled.strong`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-width: 80px;
  padding: 15px 20px;
  background: teal;
  color: white;
  border-radius: 100px;
  font-weight: bold;
  text-transform: uppercase;
  min-height: 40px;

  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease-in-out;
  &:hover {
    scale: 1.02;
  }

  /* Remove unnecessary spacing */
  i {
    margin-right: 8px;
  }
`;
